import { addDays } from "../engine/gap"
import type {
    Baustein,
    CompletedBaustein,
    EvidenceInstrument,
    LearningEvidence,
    RequiredDriver,
} from "../engine"

/**
 * Deterministic evidence for a demo population.
 *
 * The seeded roster records only that a course was finished. Real Kirkpatrick
 * evidence — a reaction score, an exam result, a transfer deliverable, a
 * manager's confirmation — is exactly the data a bank does not have on day one,
 * which is the honest starting position for the demo. So it is SYNTHESISED here
 * rather than pretended into the roster file.
 *
 * FULLY DETERMINISTIC. Every draw is a hash of `employeeId:bausteinId`, never
 * `Math.random()`, so the same person always has the same record and the
 * cockpit's numbers are reproducible between two runs of the same demo. That is
 * the same guarantee the rest of the engine makes, and the reason a switch from
 * Kirkpatrick to LTEM on stage is a measurement rather than a coin toss.
 *
 * Derived rather than stored: 1,960 people × ~7 completions is ~14,000 records,
 * and checking them into the 1.8 MB roster would roughly double it for data that
 * a hash reproduces in a few milliseconds.
 */

/** Stable 32-bit FNV-1a — mirrors `data/connectors.ts` and the summary route. */
function fnv1a(input: string): number {
    let hash = 0x811c9dc5
    for (let index = 0; index < input.length; index++) {
        hash ^= input.charCodeAt(index)
        hash = Math.imul(hash, 0x01000193)
    }
    return hash >>> 0
}

/** A stable 0–99 draw from an independent axis of the same seed. */
function draw(seed: string, axis: string): number {
    return fnv1a(`${axis}:${seed}`) % 100
}

/**
 * Which instrument a course uses when it does not say.
 *
 * Format is the honest proxy: a classroom day can assess a decision, a webinar
 * usually manages a knowledge check, and self-study assesses nothing at all.
 * This is what makes raising the evidence floor bite unevenly across the
 * catalogue — which is the point, and matches how real catalogues behave.
 */
export const INSTRUMENT_BY_FORMAT: Record<Baustein["format"], EvidenceInstrument | null> = {
    praesenz: "entscheidungstest",
    webinar: "wissenstest",
    "e-learning": "wissenstest",
    selbststudium: null,
    "on-the-job": "transfernachweis",
    social: null,
}

/**
 * Which instrument a specific Baustein actually uses.
 *
 * Pulled out so the LMS player resolves it the SAME way the seeded population
 * does — a course's declared `evidence` field wins, the format is the fallback,
 * `null` means the format assesses nothing (selbststudium, social).
 */
export function instrumentFor(baustein: Baustein): EvidenceInstrument | null {
    return baustein.evidence ?? INSTRUMENT_BY_FORMAT[baustein.format]
}

const DRIVERS: RequiredDriver[] = ["monitor", "reinforce", "encourage", "reward"]

export interface DeriveEvidenceOptions {
    /** Authored records that replace the derived one for the same Baustein. */
    overrides?: LearningEvidence[]
}

export function deriveEvidence(
    employeeId: string,
    completed: CompletedBaustein[],
    bausteinById: ReadonlyMap<string, Baustein>,
    options: DeriveEvidenceOptions = {},
): LearningEvidence[] {
    const overrides = new Map((options.overrides ?? []).map((entry) => [entry.bausteinId, entry]))

    return completed.map((completion) => {
        const authored = overrides.get(completion.bausteinId)
        if (authored) return authored

        const baustein = bausteinById.get(completion.bausteinId)
        const seed = `${employeeId}:${completion.bausteinId}`
        const evidence: LearningEvidence = { bausteinId: completion.bausteinId }

        // L1 — most people answer the survey; the ones who do skew positive,
        // which is the well-documented reason Level 1 alone proves nothing.
        if (draw(seed, "react") < 74) {
            evidence.reaction = {
                relevance: 3 + (draw(seed, "rel") % 3),
                likelihoodToApply: 3 + (draw(seed, "apply") % 3),
                on: addDays(completion.completedOn, 1),
            }
        }

        // L2 — only where the course actually assesses something. A Baustein
        // that declares its own instrument (every regulatory one does) is
        // believed over the format guess.
        const instrument = baustein ? instrumentFor(baustein) : null
        if (instrument) {
            // Mandated training is assessed and re-taken until passed, so its
            // records are near-complete and its scores cluster high. Elective
            // courses are the opposite: often no result at all, and a wider
            // spread when there is one. Flattening the two would make the
            // evidence floor bite uniformly, which is precisely what it does not
            // do in a real transcript.
            const mandated = baustein?.evidence !== undefined
            const recorded = draw(seed, "learn") < (mandated ? 97 : 84)
            if (recorded) {
                const spread = mandated ? 28 : 45
                const floor = mandated ? 0.68 : 0.52
                const scaled = Math.round((floor + (draw(seed, "score") % spread) / 100) * 100) / 100
                evidence.learning = {
                    instrument,
                    scaled,
                    passed: scaled >= 0.66,
                    on: completion.completedOn,
                }
            }
        }

        // L2→3 bridge — a deliverable applied to real work. Rarer, because it
        // takes deliberate design rather than a quiz at the end of a module.
        if (draw(seed, "bridge") < 22) {
            evidence.bridge = {
                deliverable: "Anwendung im Kundendossier",
                meetsCriteria: draw(seed, "bridgeok") < 78,
                on: addDays(completion.completedOn, 21),
            }
        }

        // L3 — confirmed transfer. Rarest of all, and the reason the manager
        // view now has a queue: this row only exists if somebody filled it in.
        if (draw(seed, "behav") < 38) {
            const roll = draw(seed, "applied")
            const driverCount = draw(seed, "drivers") % 5
            evidence.behaviour = {
                applied: roll < 58 ? "yes" : roll < 84 ? "partly" : "no",
                selfReported: true,
                ...(draw(seed, "verified") < 62 ? { verifiedBy: "manager" } : {}),
                on: addDays(completion.completedOn, 24),
                drivers: DRIVERS.slice(0, driverCount),
            }
        }

        return evidence
    })
}
